import { apiRequest } from '../../../shared/services/apiClient';
import {
  AiAssistantSettings,
  AiActionsSettings,
  Company,
  CompanyListQuery,
  Department,
  Position,
  CreateCompanyRequest,
  CreateDepartmentRequest,
  CreatePositionRequest,
  CreateLocationRequest,
  CreateRoomGroupRequest,
  CreateRoomRequest,
  CreateCompanyOpeningHourRequest,
  CreateCompanyClosureRequest,
  Location,
  LocationListQuery,
  Room,
  RoomGroup,
  RoomGroupListQuery,
  RoomListQuery,
  CompanyOpeningHour,
  CompanyOpeningHourListQuery,
  CompanyClosure,
  CompanyClosureListQuery,
  ChatBotBlockout,
  ChatBotBlockoutListQuery,
  ChatBotRoomBlockout,
  CreateChatBotBlockoutRequest,
  RegisterTelegramWebhookRequest,
  ScheduleRepeatPattern,
  TelegramSettings,
  TelegramTestSendRequest,
  TelegramTestSendResult,
  TelegramWebhookInfo,
  TelegramWebhookRegistration,
  UpdateAiActionsSettingsRequest,
  UpdateAiAssistantSettingsRequest,
  UpdateAiAssistantCommentReplyModeRequest,
  UpdateAiAssistantCommentReplyModeResponse,
  AssistantCommentReplyChannel,
  AssistantCommentReplyMode,
  BulkUpdateExternalAiAssistantMuteRequest,
  BulkUpdateExternalAiAssistantMuteResponse,
  BulkUpdateExternalAiAssistantFutureMuteResponse,
  AssistantBulkMuteChannel,
  AssistantFutureMuteChannel,
  BulkUpdateExternalAiAssistantFutureMuteRequest,
  ExternalConversationAiMuteStats,
  UpdateDepartmentRequest,
  UpdatePositionRequest,
  UpdateTelegramSettingsRequest,
  UpdateWhatsAppSettingsRequest,
  WhatsAppSettings,
  WhatsAppTestSendRequest,
  WhatsAppTestSendResult,
  UpdateInstagramSettingsRequest,
  InstagramSettings,
  InstagramOAuthStartResponse,
  InstagramTestSendRequest,
  InstagramTestSendResult,
  InstagramWebhookSubscription,
  InstagramWebhookSubscriptionStatus,
  DiscoverInstagramPagesRequest,
  DiscoverInstagramPagesResponse,
  SubscribeInstagramWebhooksRequest,
  TikTokSettings,
  UpdateTikTokSettingsRequest,
  TikTokOAuthStartResponse,
  TikTokWebhookRegistration,
  TikTokTestSendRequest,
  TikTokTestSendResult,
  FacebookMessengerSettings,
  UpdateFacebookMessengerSettingsRequest,
  FacebookMessengerTestSendRequest,
  FacebookMessengerTestSendResult,
  FacebookMessengerWebhookSubscription,
  SubscribeFacebookMessengerWebhooksRequest,
  UpdateChatBotBlockoutRequest,
  UpdateCompanyRequest,
  UpdateCompanyOpeningHourRequest,
  UpdateCompanyClosureRequest,
  UpdateLocationRequest,
  UpdateRoomGroupRequest,
  UpdateRoomRequest,
} from '../types/Company';

const pickItems = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  return [];
};

const pickSingle = (payload: any): any => {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.data && typeof payload.data === 'object') return payload.data;
  return payload;
};

const toQueryString = (query?: Record<string, string | number | boolean | undefined>): string => {
  if (!query) return '';

  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (typeof value === 'string' && !value.trim()) return;
    params.set(key, String(value));
  });

  const search = params.toString();
  return search ? `?${search}` : '';
};

const normalizeCompany = (value: any): Company => ({
  id: String(value?.id ?? ''),
  name: String(value?.name ?? ''),
  mailerFrom: value?.mailerFrom ? String(value.mailerFrom) : undefined,
  mailerFromName: value?.mailerFromName ? String(value.mailerFromName) : undefined,
  legalName: value?.legalName ? String(value.legalName) : undefined,
  email: value?.email ? String(value.email) : undefined,
  phone: value?.phone ? String(value.phone) : undefined,
  website: value?.website ? String(value.website) : undefined,
  taxId: value?.taxId ? String(value.taxId) : undefined,
  addressLine1: value?.addressLine1 ? String(value.addressLine1) : undefined,
  addressLine2: value?.addressLine2 ? String(value.addressLine2) : undefined,
  city: value?.city ? String(value.city) : undefined,
  stateOrProvince: value?.stateOrProvince ? String(value.stateOrProvince) : undefined,
  postalCode: value?.postalCode ? String(value.postalCode) : undefined,
  country: value?.country ? String(value.country) : undefined,
  language: value?.language ? String(value.language) : undefined,
  isActive: Boolean(value?.isActive),
  createdAt: String(value?.createdAt ?? ''),
  updatedAt: String(value?.updatedAt ?? ''),
});

const normalizeLocation = (value: any): Location => ({
  id: String(value?.id ?? ''),
  name: String(value?.name ?? ''),
  code: value?.code ? String(value.code) : undefined,
  email: value?.email ? String(value.email) : undefined,
  phone: value?.phone ? String(value.phone) : undefined,
  addressLine1: value?.addressLine1 ? String(value.addressLine1) : undefined,
  addressLine2: value?.addressLine2 ? String(value.addressLine2) : undefined,
  city: value?.city ? String(value.city) : undefined,
  stateOrProvince: value?.stateOrProvince ? String(value.stateOrProvince) : undefined,
  postalCode: value?.postalCode ? String(value.postalCode) : undefined,
  country: value?.country ? String(value.country) : undefined,
  latitude: typeof value?.latitude === 'number' ? value.latitude : undefined,
  longitude: typeof value?.longitude === 'number' ? value.longitude : undefined,
  checkInRadiusMeters: typeof value?.checkInRadiusMeters === 'number' ? value.checkInRadiusMeters : undefined,
  isPrimary: Boolean(value?.isPrimary),
  isActive: Boolean(value?.isActive),
  createdAt: String(value?.createdAt ?? ''),
  updatedAt: String(value?.updatedAt ?? ''),
});

const normalizeDepartment = (value: any): Department => ({
  id: String(value?.id ?? ''),
  name: String(value?.name ?? ''),
  parentDepartmentId: value?.parentDepartmentId ? String(value.parentDepartmentId) : undefined,
  isActive: Boolean(value?.isActive),
  createdAt: String(value?.createdAt ?? ''),
  updatedAt: String(value?.updatedAt ?? ''),
});

const normalizePosition = (value: any): Position => ({
  id: String(value?.id ?? ''),
  name: String(value?.name ?? ''),
  departmentId: value?.departmentId ? String(value.departmentId) : undefined,
  departmentName: value?.departmentName ? String(value.departmentName) : undefined,
  createdAt: String(value?.createdAt ?? ''),
  updatedAt: String(value?.updatedAt ?? ''),
});

const normalizeRoomGroup = (value: any): RoomGroup => ({
  id: String(value?.id ?? ''),
  locationId: String(value?.locationId ?? ''),
  name: String(value?.name ?? ''),
  description: value?.description ? String(value.description) : undefined,
  isActive: Boolean(value?.isActive),
  createdAt: String(value?.createdAt ?? ''),
  updatedAt: String(value?.updatedAt ?? ''),
});

const normalizeRoom = (value: any): Room => ({
  id: String(value?.id ?? ''),
  roomGroupId: String(value?.roomGroupId ?? ''),
  name: String(value?.name ?? ''),
  code: value?.code ? String(value.code) : undefined,
  description: value?.description ? String(value.description) : undefined,
  capacity: typeof value?.capacity === 'number' ? value.capacity : undefined,
  isActive: Boolean(value?.isActive),
  createdAt: String(value?.createdAt ?? ''),
  updatedAt: String(value?.updatedAt ?? ''),
});

const normalizeRepeatPattern = (value: any): ScheduleRepeatPattern => {
  const normalized = String(value ?? 'none').toLowerCase();
  if (normalized === 'weekly' || normalized === 'monthly' || normalized === 'yearly') {
    return normalized;
  }

  return 'none';
};

const dayIndexToApiValue = (day: number): string | null => {
  const dayMap: Record<number, string> = {
    0: 'sunday',
    1: 'monday',
    2: 'tuesday',
    3: 'wednesday',
    4: 'thursday',
    5: 'friday',
    6: 'saturday',
  };

  return dayMap[day] ?? null;
};

const apiDayValueToIndex = (day: any): number | null => {
  if (typeof day === 'number' && Number.isInteger(day) && day >= 0 && day <= 6) {
    return day;
  }

  const normalized = String(day ?? '').toLowerCase();
  const dayMap: Record<string, number> = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };

  return normalized in dayMap ? dayMap[normalized] : null;
};

const normalizeOpeningHour = (value: any): CompanyOpeningHour => ({
  id: String(value?.id ?? ''),
  locationId: value?.locationId ? String(value.locationId) : undefined,
  repeatPattern: normalizeRepeatPattern(value?.repeatPattern),
  isAllDay: Boolean(value?.isAllDay),
  daysOfWeek: Array.isArray(value?.daysOfWeek) ? value.daysOfWeek.map((day: any) => Number(day)).filter((day: number) => Number.isFinite(day)) : [],
  startDate: value?.startDate ? String(value.startDate) : undefined,
  endDate: value?.endDate ? String(value.endDate) : undefined,
  startTime: String(value?.startTime ?? ''),
  endTime: String(value?.endTime ?? ''),
  isEnabled: Boolean(value?.isEnabled),
  createdAt: String(value?.createdAt ?? ''),
  updatedAt: String(value?.updatedAt ?? ''),
});

const toApiOpeningHourPayload = (request: CreateCompanyOpeningHourRequest | UpdateCompanyOpeningHourRequest) => {
  const mappedDays = request.daysOfWeek
    ?.map(dayIndexToApiValue)
    .filter((day): day is string => Boolean(day));

  return {
    ...request,
    daysOfWeek: mappedDays,
  };
};

const normalizeOpeningHourDays = (days: any): number[] => {
  if (!Array.isArray(days)) {
    return [];
  }

  return days
    .map(apiDayValueToIndex)
    .filter((day): day is number => day !== null);
};

const normalizeOpeningHourPatched = (value: any): CompanyOpeningHour => ({
  ...normalizeOpeningHour(value),
  daysOfWeek: normalizeOpeningHourDays(value?.daysOfWeek),
});

const normalizeClosure = (value: any): CompanyClosure => ({
  id: String(value?.id ?? ''),
  locationId: value?.locationId ? String(value.locationId) : undefined,
  repeatPattern: normalizeRepeatPattern(value?.repeatPattern),
  isAllDay: Boolean(value?.isAllDay),
  startAtUtc: String(value?.startAtUtc ?? ''),
  endAtUtc: String(value?.endAtUtc ?? ''),
  isEnabled: Boolean(value?.isEnabled),
  createdAt: String(value?.createdAt ?? ''),
  updatedAt: String(value?.updatedAt ?? ''),
});

const normalizeBookingPreferenceRuleMode = (value: unknown): import('../types/Company').BookingPreferenceRuleMode => (
  String(value ?? '').toLowerCase() === 'limitedto' ? 'limitedTo' : 'never'
);

const normalizeBookingPreferenceAppliesTo = (value: unknown): import('../types/Company').BookingPreferenceAppliesTo => (
  String(value ?? '').toLowerCase() === 'general' ? 'general' : 'aiAssistant'
);

const normalizeChatBotBlockout = (value: any): ChatBotBlockout => ({
  id: String(value?.id ?? ''),
  name: String(value?.name ?? ''),
  description: value?.description ? String(value.description) : undefined,
  appointmentTypeId: value?.appointmentTypeId ? String(value.appointmentTypeId) : undefined,
  mode: normalizeBookingPreferenceRuleMode(value?.mode),
  appliesTo: normalizeBookingPreferenceAppliesTo(value?.appliesTo),
  locationId: value?.locationId ? String(value.locationId) : undefined,
  repeatPattern: normalizeRepeatPattern(value?.repeatPattern),
  isAllDay: Boolean(value?.isAllDay),
  daysOfWeek: normalizeOpeningHourDays(value?.daysOfWeek),
  startDate: value?.startDate ? String(value.startDate) : undefined,
  endDate: value?.endDate ? String(value.endDate) : undefined,
  startTime: value?.startTime ? String(value.startTime) : undefined,
  endTime: value?.endTime ? String(value.endTime) : undefined,
  roomIds: Array.isArray(value?.roomIds)
    ? value.roomIds.map((id: unknown) => String(id)).filter(Boolean)
    : [],
  isEnabled: Boolean(value?.isEnabled),
  hasConflict: Boolean(value?.hasConflict),
  conflictingRuleIds: Array.isArray(value?.conflictingRuleIds)
    ? value.conflictingRuleIds.map((id: unknown) => String(id)).filter(Boolean)
    : [],
  createdAt: String(value?.createdAt ?? ''),
  updatedAt: String(value?.updatedAt ?? ''),
});

const toApiChatBotBlockoutPayload = (request: CreateChatBotBlockoutRequest | UpdateChatBotBlockoutRequest) => {
  const mappedDays = request.daysOfWeek
    ?.map(dayIndexToApiValue)
    .filter((day): day is string => Boolean(day));

  const payload: Record<string, unknown> = {
    ...request,
    daysOfWeek: mappedDays,
  };

  if ('clearAppointmentTypeId' in request && request.clearAppointmentTypeId) {
    payload.clearAppointmentTypeId = true;
    delete payload.appointmentTypeId;
  }

  if (request.roomIds) {
    payload.roomIds = request.roomIds;
  }

  return payload;
};

const normalizeChatBotRoomBlockout = (value: any): ChatBotRoomBlockout => ({
  id: String(value?.id ?? ''),
  chatBotBlockoutId: String(value?.chatBotBlockoutId ?? ''),
  roomId: String(value?.roomId ?? ''),
  createdAt: String(value?.createdAt ?? ''),
});

const normalizeTelegramSettings = (value: any): TelegramSettings => ({
  apiBaseUrl: String(value?.apiBaseUrl ?? ''),
  hasBotToken: Boolean(value?.hasBotToken),
  hasWebhookSecretToken: Boolean(value?.hasWebhookSecretToken),
});

const normalizeTelegramWebhookRegistration = (value: any): TelegramWebhookRegistration => ({
  webhookUrl: String(value?.webhookUrl ?? ''),
  hasSecretToken: Boolean(value?.hasSecretToken),
  registeredAt: String(value?.registeredAt ?? ''),
});

const normalizeTelegramWebhookInfo = (value: any): TelegramWebhookInfo => ({
  webhookUrl: String(value?.webhookUrl ?? ''),
  hasCustomCertificate: Boolean(value?.hasCustomCertificate),
  pendingUpdateCount: Number(value?.pendingUpdateCount ?? 0),
  lastErrorMessage: value?.lastErrorMessage ? String(value.lastErrorMessage) : undefined,
  lastErrorAt: value?.lastErrorAt ? String(value.lastErrorAt) : undefined,
  lastSynchronizationErrorAt: value?.lastSynchronizationErrorAt ? String(value.lastSynchronizationErrorAt) : undefined,
  maxConnections: typeof value?.maxConnections === 'number' ? value.maxConnections : undefined,
  ipAddress: value?.ipAddress ? String(value.ipAddress) : undefined,
  allowedUpdates: Array.isArray(value?.allowedUpdates)
    ? value.allowedUpdates.map((item: any) => String(item)).filter(Boolean)
    : [],
});

const normalizeTelegramTestSendResult = (value: any): TelegramTestSendResult => ({
  chatId: String(value?.chatId ?? ''),
  messageId: String(value?.messageId ?? ''),
  sentAt: String(value?.sentAt ?? ''),
});

const normalizeWhatsAppSettings = (value: any): WhatsAppSettings => ({
  cloudApiBaseUrl: String(value?.cloudApiBaseUrl ?? ''),
  cloudApiVersion: String(value?.cloudApiVersion ?? ''),
  whatsAppAccountId: value?.whatsAppAccountId ? String(value.whatsAppAccountId) : undefined,
  cloudPhoneNumberId: value?.cloudPhoneNumberId ? String(value.cloudPhoneNumberId) : undefined,
  phoneNumber: value?.phoneNumber ? String(value.phoneNumber) : undefined,
  hasCloudAccessToken: Boolean(value?.hasCloudAccessToken),
  hasCloudWebhookVerifyToken: Boolean(value?.hasCloudWebhookVerifyToken),
  hasCloudAppSecret: Boolean(value?.hasCloudAppSecret),
  validateCloudSignature: Boolean(value?.validateCloudSignature),
});

const normalizeWhatsAppTestSendResult = (value: any): WhatsAppTestSendResult => ({
  messageSid: String(value?.messageSid ?? ''),
  to: String(value?.to ?? ''),
  status: String(value?.status ?? ''),
  sentAt: String(value?.sentAt ?? ''),
});

const normalizeInstagramSettings = (value: any): InstagramSettings => ({
  apiBaseUrl: String(value?.apiBaseUrl ?? ''),
  apiVersion: String(value?.apiVersion ?? ''),
  instagramBusinessAccountId: value?.instagramBusinessAccountId ? String(value.instagramBusinessAccountId) : undefined,
  hasPageAccessToken: Boolean(value?.hasPageAccessToken),
  hasWebhookVerifyToken: Boolean(value?.hasWebhookVerifyToken),
  hasAppId: Boolean(value?.hasAppId),
  hasAppSecret: Boolean(value?.hasAppSecret),
  validateSignature: Boolean(value?.validateSignature),
  defaultCommentReplyMode: value?.defaultCommentReplyMode === 'private_comment' ? 'private_comment' : 'public_comment',
  instagramUsername: value?.instagramUsername ? String(value.instagramUsername) : undefined,
  accessTokenExpiresAt: value?.accessTokenExpiresAt ? String(value.accessTokenExpiresAt) : undefined,
  accessTokenRefreshedAt: value?.accessTokenRefreshedAt ? String(value.accessTokenRefreshedAt) : undefined,
  connectedViaOAuth: Boolean(value?.connectedViaOAuth),
  oauthRedirectUri: value?.oauthRedirectUri ? String(value.oauthRedirectUri) : undefined,
  oauthFrontendReturnUrl: value?.oauthFrontendReturnUrl ? String(value.oauthFrontendReturnUrl) : undefined,
  suggestedOAuthRedirectUri: value?.suggestedOAuthRedirectUri ? String(value.suggestedOAuthRedirectUri) : undefined,
  suggestedOAuthFrontendReturnUrl: value?.suggestedOAuthFrontendReturnUrl ? String(value.suggestedOAuthFrontendReturnUrl) : undefined,
});

const normalizeInstagramTestSendResult = (value: any): InstagramTestSendResult => ({
  recipientId: String(value?.recipientId ?? ''),
  messageId: String(value?.messageId ?? ''),
  sentAtUtc: String(value?.sentAtUtc ?? ''),
});

const normalizeInstagramWebhookSubscription = (value: any): InstagramWebhookSubscription => ({
  instagramBusinessAccountId: String(value?.instagramBusinessAccountId ?? ''),
  subscribedFields: String(value?.subscribedFields ?? ''),
  success: Boolean(value?.success),
  subscribedAtUtc: String(value?.subscribedAtUtc ?? ''),
});

const normalizeInstagramWebhookSubscriptionStatus = (value: any): InstagramWebhookSubscriptionStatus => ({
  instagramBusinessAccountId: String(value?.instagramBusinessAccountId ?? ''),
  subscribedFields: Array.isArray(value?.subscribedFields)
    ? value.subscribedFields.map((field: unknown) => String(field))
    : [],
  appId: value?.appId ? String(value.appId) : undefined,
});

const normalizeDiscoverInstagramPagesResponse = (value: any): DiscoverInstagramPagesResponse => ({
  pages: Array.isArray(value?.pages)
    ? value.pages.map((page: any) => ({
      pageId: String(page?.pageId ?? ''),
      pageName: page?.pageName ? String(page.pageName) : undefined,
      pageAccessToken: page?.pageAccessToken ? String(page.pageAccessToken) : undefined,
      instagramBusinessAccountId: page?.instagramBusinessAccountId ? String(page.instagramBusinessAccountId) : undefined,
    }))
    : [],
});

const normalizeTikTokSettings = (value: any): TikTokSettings => ({
  apiBaseUrl: String(value?.apiBaseUrl ?? ''),
  businessOpenId: value?.businessOpenId ? String(value.businessOpenId) : undefined,
  hasAppId: Boolean(value?.hasAppId),
  hasAppSecret: Boolean(value?.hasAppSecret),
  hasAccessToken: Boolean(value?.hasAccessToken),
  validateSignature: Boolean(value?.validateSignature),
  businessUsername: value?.businessUsername ? String(value.businessUsername) : undefined,
  connectedViaOAuth: Boolean(value?.connectedViaOAuth),
  accessTokenExpiresAt: value?.accessTokenExpiresAt ? String(value.accessTokenExpiresAt) : undefined,
  accessTokenRefreshedAt: value?.accessTokenRefreshedAt ? String(value.accessTokenRefreshedAt) : undefined,
  oauthRedirectUri: value?.oauthRedirectUri ? String(value.oauthRedirectUri) : undefined,
  oauthFrontendReturnUrl: value?.oauthFrontendReturnUrl ? String(value.oauthFrontendReturnUrl) : undefined,
  suggestedOAuthRedirectUri: value?.suggestedOAuthRedirectUri ? String(value.suggestedOAuthRedirectUri) : undefined,
  suggestedOAuthFrontendReturnUrl: value?.suggestedOAuthFrontendReturnUrl ? String(value.suggestedOAuthFrontendReturnUrl) : undefined,
  suggestedWebhookUrl: value?.suggestedWebhookUrl ? String(value.suggestedWebhookUrl) : undefined,
});

const normalizeTikTokWebhookRegistration = (value: any): TikTokWebhookRegistration => ({
  callbackUrl: String(value?.callbackUrl ?? ''),
  eventType: String(value?.eventType ?? ''),
  success: Boolean(value?.success),
  registeredCallbackUrl: value?.registeredCallbackUrl ? String(value.registeredCallbackUrl) : undefined,
});

const normalizeTikTokTestSendResult = (value: any): TikTokTestSendResult => ({
  conversationId: String(value?.conversationId ?? ''),
  messageId: String(value?.messageId ?? ''),
  sentAtUtc: String(value?.sentAtUtc ?? ''),
});

const normalizeFacebookMessengerSettings = (value: any): FacebookMessengerSettings => ({
  apiBaseUrl: String(value?.apiBaseUrl ?? ''),
  apiVersion: String(value?.apiVersion ?? ''),
  pageId: value?.pageId ? String(value.pageId) : undefined,
  hasPageAccessToken: Boolean(value?.hasPageAccessToken),
  hasWebhookVerifyToken: Boolean(value?.hasWebhookVerifyToken),
  hasAppSecret: Boolean(value?.hasAppSecret),
  validateSignature: Boolean(value?.validateSignature),
});

const normalizeFacebookMessengerTestSendResult = (value: any): FacebookMessengerTestSendResult => ({
  recipientId: String(value?.recipientId ?? ''),
  messageId: String(value?.messageId ?? ''),
  sentAtUtc: String(value?.sentAtUtc ?? ''),
});

const normalizeFacebookMessengerWebhookSubscription = (value: any): FacebookMessengerWebhookSubscription => ({
  pageId: String(value?.pageId ?? ''),
  subscribedFields: String(value?.subscribedFields ?? ''),
  success: Boolean(value?.success),
  subscribedAtUtc: String(value?.subscribedAtUtc ?? ''),
});

const normalizeAiAssistantSettings = (value: any): AiAssistantSettings => ({
  displayName: String(value?.displayName ?? '').trim() || 'AI Assistant',
  isOverridden: Boolean(value?.isOverridden),
  autoRefreshAiMemoryMessageThreshold: Number.isFinite(Number(value?.autoRefreshAiMemoryMessageThreshold))
    ? Number(value.autoRefreshAiMemoryMessageThreshold)
    : 5,
  isAutoRefreshAiMemoryMessageThresholdOverridden: Boolean(value?.isAutoRefreshAiMemoryMessageThresholdOverridden),
  isBackgroundMemoryRefreshEnabled: Boolean(value?.isBackgroundMemoryRefreshEnabled),
  isBackgroundMemoryRefreshEnabledOverridden: Boolean(value?.isBackgroundMemoryRefreshEnabledOverridden),
  aiGeneratedMessageDisclaimer: String(value?.aiGeneratedMessageDisclaimer ?? '').trim(),
  isAiGeneratedMessageDisclaimerOverridden: Boolean(value?.isAiGeneratedMessageDisclaimerOverridden),
  isExternalInboundAiAssistantEnabled: Boolean(value?.isExternalInboundAiAssistantEnabled),
  isExternalInboundAiAssistantEnabledOverridden: Boolean(value?.isExternalInboundAiAssistantEnabledOverridden),
  isGlobalExternalAiAssistantMuted: value?.isGlobalExternalAiAssistantMuted === undefined
    ? true
    : Boolean(value?.isGlobalExternalAiAssistantMuted),
  isGlobalExternalAiAssistantMutedOverridden: Boolean(value?.isGlobalExternalAiAssistantMutedOverridden),
  externalInboundAiAssistantChannelSettings: normalizeExternalInboundAiAssistantChannelSettings(
    value?.externalInboundAiAssistantChannelSettings,
  ),
  aiAssistantCommentReplyModeByChannel: normalizeAiAssistantCommentReplyModeByChannel(
    value?.aiAssistantCommentReplyModeByChannel,
  ),
  showEmployeeNameOnExternalOutbound: Boolean(value?.showEmployeeNameOnExternalOutbound),
  isShowEmployeeNameOnExternalOutboundOverridden: Boolean(value?.isShowEmployeeNameOnExternalOutboundOverridden),
  isPostConversationFollowUpEnabled: Boolean(value?.isPostConversationFollowUpEnabled),
  postConversationFollowUpDelayMinutes: Number.isFinite(Number(value?.postConversationFollowUpDelayMinutes))
    ? Number(value.postConversationFollowUpDelayMinutes)
    : 60,
  isPostConversationFollowUpDelayOverridden: Boolean(value?.isPostConversationFollowUpDelayOverridden),
  isAiAgentTasksEnabled: Boolean(value?.isAiAgentTasksEnabled),
});

const normalizeAiAssistantCommentReplyModeByChannel = (
  value: unknown,
): Partial<Record<AssistantCommentReplyChannel, AssistantCommentReplyMode>> => {
  if (!value || typeof value !== 'object') {
    return {};
  }

  const channels: AssistantCommentReplyChannel[] = ['instagram', 'messenger', 'tiktok'];
  const normalized: Partial<Record<AssistantCommentReplyChannel, AssistantCommentReplyMode>> = {};

  channels.forEach(channel => {
    const channelValue = (value as Record<string, unknown>)[channel];
    if (channelValue === 'private_comment' || channelValue === 'public_comment') {
      normalized[channel] = channelValue;
    }
  });

  return normalized;
};

const normalizeExternalInboundAiAssistantChannelSettings = (
  value: unknown,
): Partial<Record<AssistantFutureMuteChannel, boolean>> => {
  if (!value || typeof value !== 'object') {
    return {};
  }

  const channels: AssistantFutureMuteChannel[] = [
    'whatsapp',
    'telegram',
    'instagram',
    'instagram_comments',
    'messenger',
    'messenger_comments',
    'webchat',
    'tiktok',
    'voice',
    'email',
  ];
  const normalized: Partial<Record<AssistantFutureMuteChannel, boolean>> = {};

  channels.forEach(channel => {
    const channelValue = (value as Record<string, unknown>)[channel];
    if (typeof channelValue === 'boolean') {
      normalized[channel] = channelValue;
    }
  });

  return normalized;
};

const normalizeExternalConversationAiMuteStats = (value: any): ExternalConversationAiMuteStats => {
  const channels: AssistantBulkMuteChannel[] = [
    'whatsapp',
    'telegram',
    'instagram',
    'messenger',
    'webchat',
    'tiktok',
    'voice',
    'email',
  ];
  const rawByChannel = value?.unmutedCountByChannel;
  const unmutedCountByChannel: Partial<Record<AssistantBulkMuteChannel, number>> = {};

  if (rawByChannel && typeof rawByChannel === 'object') {
    channels.forEach(channel => {
      const channelValue = rawByChannel[channel];
      if (Number.isFinite(Number(channelValue))) {
        unmutedCountByChannel[channel] = Number(channelValue);
      }
    });
  }

  return {
    totalUnmutedCount: Number(value?.totalUnmutedCount ?? 0),
    unmutedCountByChannel,
  };
};

const normalizeAiActionsSettings = (value: any): AiActionsSettings => ({
  allowedActions: Array.isArray(value?.allowedActions)
    ? value.allowedActions.map((item: any) => String(item)).filter(Boolean)
    : [],
  supportedActions: Array.isArray(value?.supportedActions)
    ? value.supportedActions.map((item: any) => String(item)).filter(Boolean)
    : [],
  isOverridden: Boolean(value?.isOverridden),
});

export const companyService = {
  async listCompanies(query?: CompanyListQuery): Promise<Company[]> {
    const payload = await apiRequest<any>(
      `/api/v1/companies${toQueryString({
        Q: query?.q,
        IsActive: query?.isActive,
        SortBy: query?.sortBy,
        SortDir: query?.sortDir,
        Page: query?.page,
        PageSize: query?.pageSize,
      })}`,
    );

    return pickItems(payload).map(normalizeCompany);
  },

  async createCompany(request: CreateCompanyRequest): Promise<Company | null> {
    const payload = await apiRequest<any>('/api/v1/companies', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    const item = pickSingle(payload);
    return item ? normalizeCompany(item) : null;
  },

  async updateCompany(request: UpdateCompanyRequest): Promise<Company | null> {
    const payload = await apiRequest<any>('/api/v1/companies/current', {
      method: 'PATCH',
      body: JSON.stringify(request),
    });

    const item = pickSingle(payload);
    return item ? normalizeCompany(item) : null;
  },

  async listLocations(query?: LocationListQuery): Promise<Location[]> {
    const payload = await apiRequest<any>(
      `/api/v1/companies/locations${toQueryString({
        Q: query?.q,
        IsActive: query?.isActive,
        IsPrimary: query?.isPrimary,
        SortBy: query?.sortBy,
        SortDir: query?.sortDir,
        Page: query?.page,
        PageSize: query?.pageSize,
      })}`,
    );

    return pickItems(payload).map(normalizeLocation);
  },

  async createLocation(request: CreateLocationRequest): Promise<Location | null> {
    const payload = await apiRequest<any>('/api/v1/companies/locations', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    const item = pickSingle(payload);
    return item ? normalizeLocation(item) : null;
  },

  async updateLocation(locationId: string, request: UpdateLocationRequest): Promise<Location | null> {
    const payload = await apiRequest<any>(`/api/v1/companies/locations/${locationId}`, {
      method: 'PATCH',
      body: JSON.stringify(request),
    });

    const item = pickSingle(payload);
    return item ? normalizeLocation(item) : null;
  },

  async deleteLocation(locationId: string): Promise<void> {
    await apiRequest<void>(`/api/v1/companies/locations/${locationId}`, { method: 'DELETE' });
  },

  async listDepartments(): Promise<Department[]> {
    const payload = await apiRequest<any>('/api/v1/companies/departments');
    return pickItems(payload).map(normalizeDepartment);
  },

  async createDepartment(request: CreateDepartmentRequest): Promise<Department | null> {
    const payload = await apiRequest<any>('/api/v1/companies/departments', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    const item = pickSingle(payload);
    return item ? normalizeDepartment(item) : null;
  },

  async updateDepartment(departmentId: string, request: UpdateDepartmentRequest): Promise<Department | null> {
    const payload = await apiRequest<any>(`/api/v1/companies/departments/${departmentId}`, {
      method: 'PATCH',
      body: JSON.stringify(request),
    });

    const item = pickSingle(payload);
    return item ? normalizeDepartment(item) : null;
  },

  async deleteDepartment(departmentId: string): Promise<void> {
    await apiRequest<void>(`/api/v1/companies/departments/${departmentId}`, { method: 'DELETE' });
  },

  async listPositions(): Promise<Position[]> {
    const payload = await apiRequest<any>('/api/v1/companies/positions');
    return pickItems(payload).map(normalizePosition);
  },

  async createPosition(request: CreatePositionRequest): Promise<Position | null> {
    const payload = await apiRequest<any>('/api/v1/companies/positions', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    const item = pickSingle(payload);
    return item ? normalizePosition(item) : null;
  },

  async updatePosition(positionId: string, request: UpdatePositionRequest): Promise<Position | null> {
    const payload = await apiRequest<any>(`/api/v1/companies/positions/${positionId}`, {
      method: 'PATCH',
      body: JSON.stringify(request),
    });

    const item = pickSingle(payload);
    return item ? normalizePosition(item) : null;
  },

  async deletePosition(positionId: string): Promise<void> {
    await apiRequest<void>(`/api/v1/companies/positions/${positionId}`, { method: 'DELETE' });
  },

  async listRoomGroups(locationId: string, query?: RoomGroupListQuery): Promise<RoomGroup[]> {
    const payload = await apiRequest<any>(
      `/api/v1/companies/locations/${locationId}/room-groups${toQueryString({
        Q: query?.q,
        IsActive: query?.isActive,
        SortBy: query?.sortBy,
        SortDir: query?.sortDir,
        Page: query?.page,
        PageSize: query?.pageSize,
      })}`,
    );

    return pickItems(payload).map(normalizeRoomGroup);
  },

  async createRoomGroup(locationId: string, request: CreateRoomGroupRequest): Promise<RoomGroup | null> {
    const payload = await apiRequest<any>(`/api/v1/companies/locations/${locationId}/room-groups`, {
      method: 'POST',
      body: JSON.stringify(request),
    });

    const item = pickSingle(payload);
    return item ? normalizeRoomGroup(item) : null;
  },

  async updateRoomGroup(locationId: string, roomGroupId: string, request: UpdateRoomGroupRequest): Promise<RoomGroup | null> {
    const payload = await apiRequest<any>(`/api/v1/companies/locations/${locationId}/room-groups/${roomGroupId}`, {
      method: 'PATCH',
      body: JSON.stringify(request),
    });

    const item = pickSingle(payload);
    return item ? normalizeRoomGroup(item) : null;
  },

  async deleteRoomGroup(locationId: string, roomGroupId: string): Promise<void> {
    await apiRequest<void>(`/api/v1/companies/locations/${locationId}/room-groups/${roomGroupId}`, { method: 'DELETE' });
  },

  async listRooms(locationId: string, roomGroupId: string, query?: RoomListQuery): Promise<Room[]> {
    const payload = await apiRequest<any>(
      `/api/v1/companies/locations/${locationId}/room-groups/${roomGroupId}/rooms${toQueryString({
        Q: query?.q,
        IsActive: query?.isActive,
        SortBy: query?.sortBy,
        SortDir: query?.sortDir,
        Page: query?.page,
        PageSize: query?.pageSize,
      })}`,
    );

    return pickItems(payload).map(normalizeRoom);
  },

  async createRoom(locationId: string, roomGroupId: string, request: CreateRoomRequest): Promise<Room | null> {
    const payload = await apiRequest<any>(`/api/v1/companies/locations/${locationId}/room-groups/${roomGroupId}/rooms`, {
      method: 'POST',
      body: JSON.stringify(request),
    });

    const item = pickSingle(payload);
    return item ? normalizeRoom(item) : null;
  },

  async updateRoom(locationId: string, roomGroupId: string, roomId: string, request: UpdateRoomRequest): Promise<Room | null> {
    const payload = await apiRequest<any>(`/api/v1/companies/locations/${locationId}/room-groups/${roomGroupId}/rooms/${roomId}`, {
      method: 'PATCH',
      body: JSON.stringify(request),
    });

    const item = pickSingle(payload);
    return item ? normalizeRoom(item) : null;
  },

  async deleteRoom(locationId: string, roomGroupId: string, roomId: string): Promise<void> {
    await apiRequest<void>(`/api/v1/companies/locations/${locationId}/room-groups/${roomGroupId}/rooms/${roomId}`, { method: 'DELETE' });
  },

  async listOpeningHours(query?: CompanyOpeningHourListQuery): Promise<CompanyOpeningHour[]> {
    const payload = await apiRequest<any>(
      `/api/v1/companies/business-hours/opening-hours${toQueryString({
        IsEnabled: query?.isEnabled,
        LocationId: query?.locationId,
        RepeatPattern: query?.repeatPattern,
      })}`,
    );

    return pickItems(payload).map(normalizeOpeningHourPatched);
  },

  async createOpeningHour(request: CreateCompanyOpeningHourRequest): Promise<CompanyOpeningHour | null> {
    const payload = await apiRequest<any>('/api/v1/companies/business-hours/opening-hours', {
      method: 'POST',
      body: JSON.stringify(toApiOpeningHourPayload(request)),
    });

    const item = pickSingle(payload);
    return item ? normalizeOpeningHourPatched(item) : null;
  },

  async updateOpeningHour(openingHourId: string, request: UpdateCompanyOpeningHourRequest): Promise<CompanyOpeningHour | null> {
    const payload = await apiRequest<any>(`/api/v1/companies/business-hours/opening-hours/${openingHourId}`, {
      method: 'PATCH',
      body: JSON.stringify(toApiOpeningHourPayload(request)),
    });

    const item = pickSingle(payload);
    return item ? normalizeOpeningHourPatched(item) : null;
  },

  async deleteOpeningHour(openingHourId: string): Promise<void> {
    await apiRequest<void>(`/api/v1/companies/business-hours/opening-hours/${openingHourId}`, { method: 'DELETE' });
  },

  async listClosures(query?: CompanyClosureListQuery): Promise<CompanyClosure[]> {
    const payload = await apiRequest<any>(
      `/api/v1/companies/business-hours/closures${toQueryString({
        IsEnabled: query?.isEnabled,
        LocationId: query?.locationId,
        RepeatPattern: query?.repeatPattern,
        FromUtc: query?.fromUtc,
        ToUtc: query?.toUtc,
      })}`,
    );

    return pickItems(payload).map(normalizeClosure);
  },

  async createClosure(request: CreateCompanyClosureRequest): Promise<CompanyClosure | null> {
    const payload = await apiRequest<any>('/api/v1/companies/business-hours/closures', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    const item = pickSingle(payload);
    return item ? normalizeClosure(item) : null;
  },

  async updateClosure(closureId: string, request: UpdateCompanyClosureRequest): Promise<CompanyClosure | null> {
    const payload = await apiRequest<any>(`/api/v1/companies/business-hours/closures/${closureId}`, {
      method: 'PATCH',
      body: JSON.stringify(request),
    });

    const item = pickSingle(payload);
    return item ? normalizeClosure(item) : null;
  },

  async deleteClosure(closureId: string): Promise<void> {
    await apiRequest<void>(`/api/v1/companies/business-hours/closures/${closureId}`, { method: 'DELETE' });
  },

  async listChatBotBlockouts(query?: ChatBotBlockoutListQuery): Promise<ChatBotBlockout[]> {
    const payload = await apiRequest<any>(
      `/api/v1/companies/chatbot/blockouts${toQueryString({
        Q: query?.q,
        IsEnabled: query?.isEnabled,
        SortBy: query?.sortBy,
        SortDir: query?.sortDir,
        Page: query?.page,
        PageSize: query?.pageSize,
      })}`,
    );

    return pickItems(payload).map(normalizeChatBotBlockout);
  },

  async createChatBotBlockout(request: CreateChatBotBlockoutRequest): Promise<ChatBotBlockout | null> {
    const payload = await apiRequest<any>('/api/v1/companies/chatbot/blockouts', {
      method: 'POST',
      body: JSON.stringify(toApiChatBotBlockoutPayload(request)),
    });

    const item = pickSingle(payload);
    return item ? normalizeChatBotBlockout(item) : null;
  },

  async updateChatBotBlockout(blockoutId: string, request: UpdateChatBotBlockoutRequest): Promise<ChatBotBlockout | null> {
    const payload = await apiRequest<any>(`/api/v1/companies/chatbot/blockouts/${blockoutId}`, {
      method: 'PATCH',
      body: JSON.stringify(toApiChatBotBlockoutPayload(request)),
    });

    const item = pickSingle(payload);
    return item ? normalizeChatBotBlockout(item) : null;
  },

  async deleteChatBotBlockout(blockoutId: string): Promise<void> {
    await apiRequest<void>(`/api/v1/companies/chatbot/blockouts/${blockoutId}`, { method: 'DELETE' });
  },

  async listChatBotBlockoutRooms(blockoutId: string): Promise<ChatBotRoomBlockout[]> {
    const payload = await apiRequest<any>(`/api/v1/companies/chatbot/blockouts/${blockoutId}/rooms`);
    return pickItems(payload).map(normalizeChatBotRoomBlockout);
  },

  async addChatBotBlockoutRoom(blockoutId: string, roomId: string): Promise<ChatBotRoomBlockout | null> {
    const payload = await apiRequest<any>(`/api/v1/companies/chatbot/blockouts/${blockoutId}/rooms`, {
      method: 'POST',
      body: JSON.stringify({ roomId }),
    });

    const item = pickSingle(payload);
    return item ? normalizeChatBotRoomBlockout(item) : null;
  },

  async removeChatBotBlockoutRoom(blockoutId: string, roomId: string): Promise<void> {
    await apiRequest<void>(`/api/v1/companies/chatbot/blockouts/${blockoutId}/rooms/${roomId}`, {
      method: 'DELETE',
    });
  },

  async getTelegramSettings(): Promise<TelegramSettings> {
    const payload = await apiRequest<any>('/api/v1/telegram/settings');
    return normalizeTelegramSettings(pickSingle(payload));
  },

  async updateTelegramSettings(request: UpdateTelegramSettingsRequest): Promise<TelegramSettings> {
    const payload = await apiRequest<any>('/api/v1/telegram/settings', {
      method: 'PUT',
      body: JSON.stringify(request),
    });

    return normalizeTelegramSettings(pickSingle(payload));
  },

  async registerTelegramWebhook(request: RegisterTelegramWebhookRequest, channelAccountId?: string): Promise<TelegramWebhookRegistration> {
    const query = channelAccountId?.trim() ? `?channelAccountId=${encodeURIComponent(channelAccountId.trim())}` : '';
    const payload = await apiRequest<any>(`/api/v1/telegram/settings/register-webhook${query}`, {
      method: 'POST',
      body: JSON.stringify(request),
    });

    return normalizeTelegramWebhookRegistration(pickSingle(payload));
  },

  async getTelegramWebhookInfo(channelAccountId?: string): Promise<TelegramWebhookInfo> {
    const query = channelAccountId?.trim() ? `?channelAccountId=${encodeURIComponent(channelAccountId.trim())}` : '';
    const payload = await apiRequest<any>(`/api/v1/telegram/settings/webhook-info${query}`);
    return normalizeTelegramWebhookInfo(pickSingle(payload));
  },

  async sendTelegramTestMessage(request: TelegramTestSendRequest): Promise<TelegramTestSendResult> {
    const payload = await apiRequest<any>('/api/v1/telegram/settings/test-send', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    return normalizeTelegramTestSendResult(pickSingle(payload));
  },

  async getWhatsAppSettings(): Promise<WhatsAppSettings> {
    const payload = await apiRequest<any>('/api/v1/whatsapp/cloud/settings');
    return normalizeWhatsAppSettings(pickSingle(payload));
  },

  async updateWhatsAppSettings(request: UpdateWhatsAppSettingsRequest): Promise<WhatsAppSettings> {
    const payload = await apiRequest<any>('/api/v1/whatsapp/cloud/settings', {
      method: 'PUT',
      body: JSON.stringify(request),
    });

    return normalizeWhatsAppSettings(pickSingle(payload));
  },

  async sendWhatsAppTestMessage(request: WhatsAppTestSendRequest): Promise<WhatsAppTestSendResult> {
    const payload = await apiRequest<any>('/api/v1/whatsapp/cloud/settings/test-send', {
      method: 'POST',
      body: JSON.stringify({
        type: request.type ?? 'text',
        to: request.to,
        body: request.body,
        ...(request.previewUrl !== undefined ? { previewUrl: request.previewUrl } : {}),
      }),
    });

    return normalizeWhatsAppTestSendResult(pickSingle(payload));
  },

  async getInstagramSettings(): Promise<InstagramSettings> {
    const payload = await apiRequest<any>('/api/v1/instagram/settings');
    return normalizeInstagramSettings(pickSingle(payload));
  },

  async updateInstagramSettings(request: UpdateInstagramSettingsRequest): Promise<InstagramSettings> {
    const payload = await apiRequest<any>('/api/v1/instagram/settings', {
      method: 'PUT',
      body: JSON.stringify(request),
    });

    return normalizeInstagramSettings(pickSingle(payload));
  },

  async startInstagramOAuth(): Promise<InstagramOAuthStartResponse> {
    const payload = await apiRequest<any>('/api/v1/instagram/settings/oauth/start', {
      method: 'POST',
    });

    const normalized = pickSingle(payload);
    return {
      authorizeUrl: String(normalized?.authorizeUrl ?? ''),
      redirectUri: String(normalized?.redirectUri ?? ''),
    };
  },

  async sendInstagramTestMessage(request: InstagramTestSendRequest): Promise<InstagramTestSendResult> {
    const payload = await apiRequest<any>('/api/v1/instagram/settings/test-send', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    return normalizeInstagramTestSendResult(pickSingle(payload));
  },

  async subscribeInstagramWebhooks(request?: SubscribeInstagramWebhooksRequest): Promise<InstagramWebhookSubscription> {
    const payload = await apiRequest<any>('/api/v1/instagram/settings/subscribe-webhooks', {
      method: 'POST',
      body: JSON.stringify(request ?? {}),
    });

    return normalizeInstagramWebhookSubscription(pickSingle(payload));
  },

  async getInstagramWebhookSubscriptions(channelAccountId?: string): Promise<InstagramWebhookSubscriptionStatus> {
    const query = channelAccountId ? `?channelAccountId=${encodeURIComponent(channelAccountId)}` : '';
    const payload = await apiRequest<any>(`/api/v1/instagram/settings/webhook-subscriptions${query}`);
    return normalizeInstagramWebhookSubscriptionStatus(pickSingle(payload));
  },

  async unsubscribeInstagramWebhooks(channelAccountId?: string): Promise<InstagramWebhookSubscription> {
    const query = channelAccountId ? `?channelAccountId=${encodeURIComponent(channelAccountId)}` : '';
    const payload = await apiRequest<any>(`/api/v1/instagram/settings/webhook-subscriptions${query}`, {
      method: 'DELETE',
    });

    return normalizeInstagramWebhookSubscription(pickSingle(payload));
  },

  async discoverInstagramPages(request: DiscoverInstagramPagesRequest): Promise<DiscoverInstagramPagesResponse> {
    const payload = await apiRequest<any>('/api/v1/instagram/settings/discover-pages', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    return normalizeDiscoverInstagramPagesResponse(pickSingle(payload));
  },

  async getTikTokSettings(): Promise<TikTokSettings> {
    const payload = await apiRequest<any>('/api/v1/tiktok/settings');
    return normalizeTikTokSettings(pickSingle(payload));
  },

  async updateTikTokSettings(request: UpdateTikTokSettingsRequest): Promise<TikTokSettings> {
    const payload = await apiRequest<any>('/api/v1/tiktok/settings', {
      method: 'PUT',
      body: JSON.stringify(request),
    });

    return normalizeTikTokSettings(pickSingle(payload));
  },

  async startTikTokOAuth(): Promise<TikTokOAuthStartResponse> {
    const payload = await apiRequest<any>('/api/v1/tiktok/settings/oauth/start', {
      method: 'POST',
    });

    const normalized = pickSingle(payload);
    return {
      authorizeUrl: String(normalized?.authorizeUrl ?? ''),
      redirectUri: String(normalized?.redirectUri ?? ''),
    };
  },

  async getTikTokWebhookRegistration(): Promise<TikTokWebhookRegistration> {
    const payload = await apiRequest<any>('/api/v1/tiktok/settings/webhook');
    return normalizeTikTokWebhookRegistration(pickSingle(payload));
  },

  async registerTikTokWebhook(): Promise<TikTokWebhookRegistration> {
    const payload = await apiRequest<any>('/api/v1/tiktok/settings/register-webhook', {
      method: 'POST',
    });

    return normalizeTikTokWebhookRegistration(pickSingle(payload));
  },

  async sendTikTokTestMessage(request: TikTokTestSendRequest): Promise<TikTokTestSendResult> {
    const payload = await apiRequest<any>('/api/v1/tiktok/settings/test-send', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    return normalizeTikTokTestSendResult(pickSingle(payload));
  },

  async getFacebookMessengerSettings(): Promise<FacebookMessengerSettings> {
    const payload = await apiRequest<any>('/api/v1/messenger/settings');
    return normalizeFacebookMessengerSettings(pickSingle(payload));
  },

  async updateFacebookMessengerSettings(request: UpdateFacebookMessengerSettingsRequest): Promise<FacebookMessengerSettings> {
    const payload = await apiRequest<any>('/api/v1/messenger/settings', {
      method: 'PUT',
      body: JSON.stringify(request),
    });

    return normalizeFacebookMessengerSettings(pickSingle(payload));
  },

  async subscribeFacebookMessengerWebhooks(request?: SubscribeFacebookMessengerWebhooksRequest): Promise<FacebookMessengerWebhookSubscription> {
    const payload = await apiRequest<any>('/api/v1/messenger/settings/subscribe-webhooks', {
      method: 'POST',
      body: JSON.stringify(request ?? {}),
    });

    return normalizeFacebookMessengerWebhookSubscription(pickSingle(payload));
  },

  async sendFacebookMessengerTestMessage(request: FacebookMessengerTestSendRequest): Promise<FacebookMessengerTestSendResult> {
    const payload = await apiRequest<any>('/api/v1/messenger/settings/test-send', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    return normalizeFacebookMessengerTestSendResult(pickSingle(payload));
  },

  async getAiAssistantSettings(): Promise<AiAssistantSettings> {
    const payload = await apiRequest<any>('/api/v1/conversations/settings/assistant');
    return normalizeAiAssistantSettings(pickSingle(payload));
  },

  async getExistingExternalConversationMuteStats(): Promise<ExternalConversationAiMuteStats> {
    const payload = await apiRequest<any>('/api/v1/conversations/settings/assistant/existing-mute-stats');
    return normalizeExternalConversationAiMuteStats(pickSingle(payload));
  },

  async updateAiAssistantSettings(request: UpdateAiAssistantSettingsRequest): Promise<AiAssistantSettings> {
    const payload = await apiRequest<any>('/api/v1/conversations/settings/assistant', {
      method: 'PUT',
      body: JSON.stringify(request),
    });

    return normalizeAiAssistantSettings(pickSingle(payload));
  },

  async bulkUpdateExistingAiAssistantMute(
    request: BulkUpdateExternalAiAssistantMuteRequest,
  ): Promise<BulkUpdateExternalAiAssistantMuteResponse> {
    const payload = await apiRequest<any>('/api/v1/conversations/settings/assistant/bulk-update-existing-mute', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    const value = pickSingle(payload);
    return {
      updatedConversationCount: Number(value?.updatedConversationCount ?? 0),
      channel: value?.channel ? String(value.channel) : null,
      unmutedConversationCount: Number(value?.unmutedConversationCount ?? 0),
    };
  },

  async bulkUpdateFutureAiAssistantMute(
    request: BulkUpdateExternalAiAssistantFutureMuteRequest,
  ): Promise<BulkUpdateExternalAiAssistantFutureMuteResponse> {
    const payload = await apiRequest<any>('/api/v1/conversations/settings/assistant/bulk-update-future-mute', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    const value = pickSingle(payload);
    return {
      inboundAiEnabled: Boolean(value?.inboundAiEnabled),
      channel: value?.channel ? String(value.channel) : null,
    };
  },

  async updateAiAssistantCommentReplyMode(
    request: UpdateAiAssistantCommentReplyModeRequest,
  ): Promise<UpdateAiAssistantCommentReplyModeResponse> {
    const payload = await apiRequest<any>('/api/v1/conversations/settings/assistant/update-comment-reply-mode', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    const value = pickSingle(payload);
    return {
      mode: value?.mode === 'private_comment' ? 'private_comment' : 'public_comment',
      channel: value?.channel ? String(value.channel) : null,
    };
  },

  async getAiActionsSettings(): Promise<AiActionsSettings> {
    const payload = await apiRequest<any>('/api/v1/conversations/settings/copilot-actions');
    return normalizeAiActionsSettings(pickSingle(payload));
  },

  async updateAiActionsSettings(request: UpdateAiActionsSettingsRequest): Promise<AiActionsSettings> {
    const payload = await apiRequest<any>('/api/v1/conversations/settings/copilot-actions', {
      method: 'PUT',
      body: JSON.stringify(request),
    });

    return normalizeAiActionsSettings(pickSingle(payload));
  },
};

export const listCompanies = companyService.listCompanies;
export const createCompany = companyService.createCompany;
export const updateCompany = companyService.updateCompany;
export const listLocations = companyService.listLocations;
export const createLocation = companyService.createLocation;
export const updateLocation = companyService.updateLocation;
export const deleteLocation = companyService.deleteLocation;
export const listDepartments = companyService.listDepartments;
export const createDepartment = companyService.createDepartment;
export const updateDepartment = companyService.updateDepartment;
export const deleteDepartment = companyService.deleteDepartment;
export const listPositions = companyService.listPositions;
export const createPosition = companyService.createPosition;
export const updatePosition = companyService.updatePosition;
export const deletePosition = companyService.deletePosition;
export const listRoomGroups = companyService.listRoomGroups;
export const createRoomGroup = companyService.createRoomGroup;
export const updateRoomGroup = companyService.updateRoomGroup;
export const deleteRoomGroup = companyService.deleteRoomGroup;
export const listRooms = companyService.listRooms;
export const createRoom = companyService.createRoom;
export const updateRoom = companyService.updateRoom;
export const deleteRoom = companyService.deleteRoom;
export const listOpeningHours = companyService.listOpeningHours;
export const createOpeningHour = companyService.createOpeningHour;
export const updateOpeningHour = companyService.updateOpeningHour;
export const deleteOpeningHour = companyService.deleteOpeningHour;
export const listClosures = companyService.listClosures;
export const createClosure = companyService.createClosure;
export const updateClosure = companyService.updateClosure;
export const deleteClosure = companyService.deleteClosure;
export const listChatBotBlockouts = companyService.listChatBotBlockouts;
export const createChatBotBlockout = companyService.createChatBotBlockout;
export const updateChatBotBlockout = companyService.updateChatBotBlockout;
export const deleteChatBotBlockout = companyService.deleteChatBotBlockout;
export const listChatBotBlockoutRooms = companyService.listChatBotBlockoutRooms;
export const addChatBotBlockoutRoom = companyService.addChatBotBlockoutRoom;
export const removeChatBotBlockoutRoom = companyService.removeChatBotBlockoutRoom;
export const getTelegramSettings = companyService.getTelegramSettings;
export const updateTelegramSettings = companyService.updateTelegramSettings;
export const registerTelegramWebhook = companyService.registerTelegramWebhook;
export const getTelegramWebhookInfo = companyService.getTelegramWebhookInfo;
export const sendTelegramTestMessage = companyService.sendTelegramTestMessage;
export const getWhatsAppSettings = companyService.getWhatsAppSettings;
export const updateWhatsAppSettings = companyService.updateWhatsAppSettings;
export const sendWhatsAppTestMessage = companyService.sendWhatsAppTestMessage;
export const getInstagramSettings = companyService.getInstagramSettings;
export const updateInstagramSettings = companyService.updateInstagramSettings;
export const startInstagramOAuth = companyService.startInstagramOAuth;
export const sendInstagramTestMessage = companyService.sendInstagramTestMessage;
export const subscribeInstagramWebhooks = companyService.subscribeInstagramWebhooks;
export const getInstagramWebhookSubscriptions = companyService.getInstagramWebhookSubscriptions;
export const unsubscribeInstagramWebhooks = companyService.unsubscribeInstagramWebhooks;
export const discoverInstagramPages = companyService.discoverInstagramPages;
export const getTikTokSettings = companyService.getTikTokSettings;
export const updateTikTokSettings = companyService.updateTikTokSettings;
export const startTikTokOAuth = companyService.startTikTokOAuth;
export const getTikTokWebhookRegistration = companyService.getTikTokWebhookRegistration;
export const registerTikTokWebhook = companyService.registerTikTokWebhook;
export const sendTikTokTestMessage = companyService.sendTikTokTestMessage;
export const getFacebookMessengerSettings = companyService.getFacebookMessengerSettings;
export const updateFacebookMessengerSettings = companyService.updateFacebookMessengerSettings;
export const subscribeFacebookMessengerWebhooks = companyService.subscribeFacebookMessengerWebhooks;
export const sendFacebookMessengerTestMessage = companyService.sendFacebookMessengerTestMessage;
export const getAiAssistantSettings = companyService.getAiAssistantSettings;
export const getExistingExternalConversationMuteStats = companyService.getExistingExternalConversationMuteStats;
export const updateAiAssistantSettings = companyService.updateAiAssistantSettings;
export const bulkUpdateExistingAiAssistantMute = companyService.bulkUpdateExistingAiAssistantMute;
export const bulkUpdateFutureAiAssistantMute = companyService.bulkUpdateFutureAiAssistantMute;
export const updateAiAssistantCommentReplyMode = companyService.updateAiAssistantCommentReplyMode;
export const getAiActionsSettings = companyService.getAiActionsSettings;
export const updateAiActionsSettings = companyService.updateAiActionsSettings;
